import { useCallback, useEffect, useState } from 'react'

import {
  api,
  type AdminApp,
  type OrgDetail,
  type OrgStats,
  type PlatformTotals,
} from '../api'

// Суперадминка: сводка по платформе, аккаунты со статистикой и настройками,
// блокировка работы аккаунта.
export function Admin() {
  const [registrationOpen, setRegistrationOpen] = useState(true)
  const [totals, setTotals] = useState<PlatformTotals | null>(null)
  const [orgs, setOrgs] = useState<OrgStats[]>([])
  const [openOrg, setOpenOrg] = useState<string | null>(null)
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    try {
      const [settings, list] = await Promise.all([api.adminSettings(), api.orgs()])
      setRegistrationOpen(settings.registration_open)
      setTotals(settings.totals)
      setOrgs(list.orgs ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить данные платформы')
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  async function toggleRegistration() {
    try {
      const result = await api.setRegistrationOpen(!registrationOpen)
      setRegistrationOpen(result.registration_open)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось изменить настройку')
    }
  }

  async function setStatus(org: OrgStats, status: 'active' | 'suspended') {
    const action = status === 'suspended' ? 'Заблокировать' : 'Разблокировать'
    if (!confirm(`${action} аккаунт «${org.name}»?`)) return
    try {
      await api.setOrgStatus(org.id, status)
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось изменить статус')
    }
  }

  return (
    <div className="stack">
      <div>
        <h1>Платформа</h1>
        <p className="muted">Сводка, аккаунты и управление доступом. Видно только суперадмину.</p>
      </div>

      {error && <div className="notice error">{error}</div>}

      {totals && (
        <div className="grid">
          <Tile label="Аккаунты" value={totals.orgs} note={totals.suspended_orgs ? `${totals.suspended_orgs} заблокировано` : undefined} />
          <Tile label="Приложения" value={totals.apps} />
          <Tile label="Подписки" value={totals.subscribed} note={`всего устройств ${totals.subscriptions}`} />
          <Tile label="Отправлено за 30 дней" value={totals.sent_30d} note={totals.failed_30d ? `ошибок ${totals.failed_30d}` : undefined} />
        </div>
      )}

      <div className="card row">
        <div style={{ flex: 1 }}>
          <strong>Свободная регистрация</strong>
          <p className="muted">
            {registrationOpen
              ? 'Любой может создать аккаунт.'
              : 'Регистрация закрыта: новые аккаунты создаются только вручную.'}
          </p>
        </div>
        <button className="primary" onClick={toggleRegistration}>
          {registrationOpen ? 'Закрыть регистрацию' : 'Открыть регистрацию'}
        </button>
      </div>

      <div className="card stack">
        <h2>Аккаунты</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Аккаунт</th>
                <th>Статус</th>
                <th>Приложения</th>
                <th>Подписки</th>
                <th>Отправлено 30д</th>
                <th>Клики</th>
                <th>Ошибки</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orgs.map((org) => (
                <tr key={org.id}>
                  <td>
                    <button className="link" onClick={() => setOpenOrg(openOrg === org.id ? null : org.id)}>
                      {org.name}
                    </button>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {org.members} участн. · с {new Date(org.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td>
                    {org.status === 'active' ? (
                      <span className="badge ok">активен</span>
                    ) : (
                      <span className="badge bad">заблокирован</span>
                    )}
                  </td>
                  <td>{org.apps}</td>
                  <td>
                    {org.subscribed}
                    <span className="muted"> / {org.subscriptions}</span>
                  </td>
                  <td>{org.sent_30d}</td>
                  <td>{org.clicked_30d}</td>
                  <td>{org.failed_30d}</td>
                  <td>
                    {org.status === 'active' ? (
                      <button className="link" onClick={() => setStatus(org, 'suspended')}>
                        Заблокировать
                      </button>
                    ) : (
                      <button className="link" onClick={() => setStatus(org, 'active')}>
                        Разблокировать
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {orgs.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">
                    Аккаунтов пока нет.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openOrg && <OrgCard orgId={openOrg} onClose={() => setOpenOrg(null)} />}
    </div>
  )
}

function Tile({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="card tile">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
      {note && <div className="label muted">{note}</div>}
    </div>
  )
}

// Карточка аккаунта: участники, приложения со всеми настройками и кредами,
// журнал действий.
function OrgCard({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const [data, setData] = useState<OrgDetail | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api
      .org(orgId)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить аккаунт'))
  }, [orgId])

  if (error) return <div className="notice error">{error}</div>
  if (!data) return <div className="card muted">Загрузка карточки аккаунта…</div>

  return (
    <div className="card stack">
      <div className="row">
        <h2 style={{ flex: 1, margin: 0 }}>
          {data.org.name}{' '}
          {data.org.status !== 'active' && <span className="badge bad">заблокирован</span>}
        </h2>
        <button className="link" onClick={onClose}>
          Свернуть
        </button>
      </div>

      <div>
        <h3>Участники</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Роль</th>
                <th>Последний вход</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id}>
                  <td>
                    {m.email} {m.is_superadmin && <span className="badge">суперадмин</span>}
                  </td>
                  <td>{m.role}</td>
                  <td className="muted">
                    {m.last_login_at ? new Date(m.last_login_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3>Приложения</h3>
        <div className="stack">
          {data.apps.map((app) => (
            <AppCard key={app.id} app={app} />
          ))}
          {data.apps.length === 0 && <p className="muted">Приложений нет.</p>}
        </div>
      </div>

      {data.audit.length > 0 && (
        <div>
          <h3>Журнал действий</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Действие</th>
                  <th>Объект</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {data.audit.slice(0, 15).map((a) => (
                  <tr key={a.id}>
                    <td className="muted">{new Date(a.created_at).toLocaleString()}</td>
                    <td className="mono">{a.action}</td>
                    <td className="muted">{a.target}</td>
                    <td className="muted">{a.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function AppCard({ app }: { app: AdminApp }) {
  return (
    <div className="card">
      <div className="row">
        <strong style={{ flex: 1 }}>{app.name}</strong>
        <code>{app.id}</code>
      </div>

      <div className="grid" style={{ marginTop: 12 }}>
        <Metric label="Подписки" value={`${app.subscribed} / ${app.subscriptions}`} />
        <Metric label="Мёртвые токены" value={app.invalid} />
        <Metric label="Отправлено 30д" value={app.sent_30d} />
        <Metric label="Ошибки 30д" value={app.failed_30d} />
        <Metric label="Рассылок всего" value={app.notifications} />
        <Metric label="API-ключей" value={app.api_keys} />
      </div>

      <div style={{ marginTop: 12 }}>
        <h3>Платформы</h3>
        {app.channels.length === 0 && <p className="muted">Каналы не настроены.</p>}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Канал</th>
                <th>Включён</th>
                <th>Креды</th>
                <th>Настройки</th>
              </tr>
            </thead>
            <tbody>
              {app.channels.map((c) => (
                <tr key={c.channel}>
                  <td>{c.channel}</td>
                  <td>{c.enabled ? <span className="badge ok">да</span> : <span className="badge">нет</span>}</td>
                  <td>
                    {c.has_credential ? (
                      <>
                        <span className="badge ok">{c.credential_kind}</span>{' '}
                        <code>{c.fingerprint}</code>
                        {c.expires_at && (
                          <div className="muted" style={{ fontSize: 12 }}>
                            до {new Date(c.expires_at).toLocaleDateString()}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="badge bad">не загружены</span>
                    )}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {Object.entries(c.config)
                      .filter(([, v]) => v)
                      .map(([k, v]) => `${k}: ${String(v).slice(0, 24)}`)
                      .join(' · ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div className="muted" style={{ fontSize: 13 }}>
        {label}
      </div>
    </div>
  )
}
